import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Stack, Typography } from "@mui/material";

interface RiskScorePositionLabelProps {
  scoreString: string;
  isWarning: boolean;
  /** 0 から 100 まで */
  position: number;
  unitName: string;
}

function RiskScorePositionLabel({
  scoreString,
  isWarning,
  position,
  unitName,
}: RiskScorePositionLabelProps) {
  if (position > 100 || position < 0) {
    return <></>;
  }
  return (
    <Stack
      sx={{
        width: "fit-content",
        transform: "translateX(-50%)",
        position: "absolute",
        top: "-43px",
        zIndex: 1,
        left: `${position}%`,
      }}
    >
      <Stack
        direction="row"
        sx={{
          height: "30px",
          alignItems: "baseline",
          marginBottom: "4px",
          "& p": {
            color: isWarning ? "error.main" : "text.primary",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "26px",
            fontWeight: 500,
            height: "26px",
            lineHeight: "initial",
          }}
        >
          {scoreString}
        </Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
          {unitName}
        </Typography>
      </Stack>
      <PlayArrowIcon
        sx={{
          rotate: "90deg",
          width: "16px",
          height: "16px",
          alignSelf: "center",
          filter:
            "drop-shadow(0 0 1px white) drop-shadow(0 0 1px white) drop-shadow(0 0 1px white)",
        }}
      />
    </Stack>
  );
}

interface ODI3RiskLevelChartProps {
  odi3Score: number | null;
}
export function ODI3RiskLevelChart({ odi3Score }: ODI3RiskLevelChartProps) {
  return (
    <Stack sx={{ position: "relative" }}>
      <Stack
        sx={{
          width: "96%",
          ml: "8px",
          mt: "50px",
        }}
      >
        <Stack
          direction="row"
          sx={{
            "& > div:nth-of-type(1)": { borderRadius: "8px 0 0 8px" },
            "& > div:nth-of-type(4)": { borderRadius: "0 8px 8px 0" },
          }}
        >
          <Stack
            sx={{
              width: "calc(100% / 7)",
              backgroundColor: "#0056A080",
              height: "16px",
              position: "relative",
            }}
          >
            {odi3Score == null && (
              <RiskScorePositionLabel
                scoreString="-- "
                isWarning={false}
                position={0}
                unitName="回"
              />
            )}
            {odi3Score != null && odi3Score < 5 && (
              <RiskScorePositionLabel
                scoreString={`${odi3Score}`}
                isWarning={false}
                position={(odi3Score / 5) * 100}
                unitName="回"
              />
            )}
          </Stack>
          <Stack
            sx={{
              width: "calc((100% / 7) * 2)",
              backgroundColor: "#FFCE80",
              height: "16px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 5 && odi3Score < 15 && (
              <RiskScorePositionLabel
                scoreString={`${odi3Score}`}
                isWarning={true}
                position={((odi3Score - 5) / 10) * 100}
                unitName="回"
              />
            )}
          </Stack>
          <Stack
            sx={{
              width: "calc((100% / 7) * 3)",
              backgroundColor: "#FF3A3080",
              height: "16px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 15 && odi3Score < 30 && (
              <RiskScorePositionLabel
                scoreString={`${odi3Score}`}
                isWarning={true}
                position={((odi3Score - 15) / 15) * 100}
                unitName="回"
              />
            )}
          </Stack>
          <Stack
            sx={{
              width: "calc(100% / 7)",
              backgroundColor: "#FF756E",
              height: "16px",
              position: "relative",
            }}
          >
            {odi3Score != null && odi3Score >= 30 && (
              <RiskScorePositionLabel
                scoreString={`${odi3Score}`}
                isWarning={true}
                position={(((odi3Score >= 34 ? 34 : odi3Score) - 30) / 4) * 100}
                unitName="回"
              />
            )}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            "& p": {
              fontSize: "13px",
              fontWeight: 500,
            },
          }}
        >
          <Stack sx={{ width: "calc(100% / 7)" }}>
            <Typography>0</Typography>
          </Stack>
          <Stack sx={{ width: "calc((100% / 7) * 2)" }}>
            <Typography>5</Typography>
          </Stack>
          <Stack sx={{ width: "calc((100% / 7) * 3)" }}>
            <Typography>15</Typography>
          </Stack>
          <Stack sx={{ width: "calc(100% / 7)" }}>
            <Typography>30〜</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface InsomniaRiskLevelChartProps {
  insomniaScore: number | null;
}
export function InsomniaRiskLevelChart({
  insomniaScore,
}: InsomniaRiskLevelChartProps) {
  return (
    <Stack sx={{ position: "relative" }}>
      <Stack
        sx={{
          width: "96%",
          ml: "8px",
          mt: "50px",
        }}
      >
        <Stack
          direction="row"
          sx={{
            "& > div:nth-of-type(1)": { borderRadius: "8px 0 0 8px" },
            "& > div:nth-of-type(2)": { borderRadius: "0 8px 8px 0" },
          }}
        >
          <Stack
            sx={{
              width: "calc(100% / 5)",
              backgroundColor: "#0056A080",
              height: "16px",
              position: "relative",
            }}
          >
            {insomniaScore == null && (
              <RiskScorePositionLabel
                scoreString="-- "
                isWarning={false}
                position={0}
                unitName="点"
              />
            )}
            {insomniaScore != null && insomniaScore < 6 && (
              <RiskScorePositionLabel
                scoreString={`${insomniaScore}`}
                isWarning={false}
                position={(insomniaScore / 6) * 100}
                unitName="点"
              />
            )}
          </Stack>
          <Stack
            sx={{
              width: "calc((100% / 5) * 4)",
              backgroundColor: "#FF3A3080",
              height: "16px",
              position: "relative",
            }}
          >
            {insomniaScore != null && insomniaScore >= 6 && (
              <RiskScorePositionLabel
                scoreString={`${insomniaScore}`}
                isWarning={true}
                position={
                  (((insomniaScore >= 24 ? 24 : insomniaScore) - 6) / 18) * 100
                }
                unitName="点"
              />
            )}
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            "& p": {
              fontSize: "13px",
              fontWeight: 500,
            },
          }}
        >
          <Stack sx={{ width: "calc(100% / 5)" }}>
            <Typography>0点</Typography>
          </Stack>
          <Stack sx={{ width: "calc((100% / 5) * 4 - 30px)" }}>
            <Typography>6点</Typography>
          </Stack>
          <Stack>
            <Typography>24点</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
